import { FC, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { Button, ButtonGroup } from "@mui/material";

import PieChartCard from "./PieChartCard";
import { useObservabilityStore } from "../../../../hooks/useObservabilityStore";
import { formatAgentLabel } from "../../../helpers/observabilityHelper";
import { formatCurrency } from "./MetricTiles";

type ByTypeView = "cases" | "cost";

const formatCount = (value: number): string => value.toLocaleString();

const VIEWS: { id: ByTypeView; label: string }[] = [
  { id: "cases", label: "Cases" },
  { id: "cost", label: "Cost" },
];

// Cases and Cost share the same per-classification breakdown, so they live in
// one pie with a Cases/Cost toggle instead of two separate charts.
const ByTypePie: FC = observer(() => {
  const { isUsageByAgentLoading, usageByAgent, loadUsageByAgent } =
    useObservabilityStore();
  const [view, setView] = useState<ByTypeView>("cases");

  useEffect(() => {
    loadUsageByAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slices = useMemo(
    () =>
      (usageByAgent ?? []).map((row) => ({
        name: formatAgentLabel(row.classification),
        value: view === "cases" ? row.cases : row.total_cost ?? 0,
      })),
    [usageByAgent, view],
  );

  const controls = (
    <ButtonGroup size="small" sx={{ flexShrink: 0 }}>
      {VIEWS.map((item) => (
        <Button
          key={item.id}
          variant={view === item.id ? "contained" : "outlined"}
          onClick={() => setView(item.id)}
          sx={{ textTransform: "none" }}
        >
          {item.label}
        </Button>
      ))}
    </ButtonGroup>
  );

  return (
    <PieChartCard
      title="By Type"
      slices={slices}
      formatValue={view === "cases" ? formatCount : formatCurrency}
      csvFilename={view === "cases" ? "cases-by-type" : "cost-by-type"}
      csvValueHeader={view === "cases" ? "Cases" : "Total cost"}
      emptyMessage="No data available"
      isLoading={isUsageByAgentLoading}
      controls={controls}
      fullPie
      csvSelectedOnly
    />
  );
});

export default ByTypePie;
